import { evaluateChatHeight } from "../actions/evaluateChatHeight";
import { handleAjaxError } from "../actions/handleAjaxError";
import { filterSettingsVoicesByProvider } from "../actions/playMessage";
import { initRoleplayDetailsCollapseActions } from "./roleplayDetailsCollapse";
import { getChatAjaxConfig, hydrateChatAjaxConfigFromDom } from "./chatSettingsAjaxConfig";
import { bindMemoriesManager } from "./chatSettingsMemoriesUi";
import { bindBackupsManager } from "./chatSettingsBackups";
import { bindSettingsModalNavigation, openSettingsModalFromHash } from "./chatSettingsModalNavigation";
import { bindSliderInputHandler, initPersonalitySliders } from "./chatSettingsPersonality";
import { resolveRoleplaySettingsRefresh, resolveRoleplaySettingsSubmission } from "./chatSettingsRoleplay.cjs";
import { bindRoleplayPreviewModal, updateRoleplayRowUI } from "./chatSettingsRoleplayPreview";
import {
    bindVoicePreviewHandlers,
    bindVoiceSelectPreviewHandlers,
    stopVoicePreview,
} from "./chatSettingsVoice";

const set_roleplay_background = true;
const ROLEPLAY_SCENARIO_KEY = getChatAjaxConfig().roleplay_scenario_key || 'roleplay_scenario';
const LAST_EMBEDDED_ROLEPLAY_SCENARIO_KEY = 'last_embedded_roleplay_scenario';
const BASE_BACKGROUND_STORAGE_PREFIX = 'growtype_chat_base_background:';
const auto_enable_roleplay_on_scenario_change = typeof window.auto_enable_roleplay_on_scenario_change !== 'undefined'
    ? window.auto_enable_roleplay_on_scenario_change
    : (getChatAjaxConfig().auto_enable_roleplay_on_scenario_change ?? false);
let currentBackgroundValue = null;
let activeRoleplayKey = null;
let baseBackgroundState = null;

function getCanonicalRoleplayHeaderWrapper($header) {
    const $wrappers = $header.find('.roleplay-details-wrapper');

    if ($wrappers.length < 2) {
        return $wrappers.first();
    }

    const $canonicalWrapper = $wrappers.last();
    $wrappers.not($canonicalWrapper).remove();

    return $canonicalWrapper;
}

function syncRoleplayHeaderWrapperInPlace($targetWrapper, $sourceWrapper) {
    if (!$targetWrapper?.length || !$sourceWrapper?.length) {
        return $targetWrapper;
    }

    const sourceAttributes = new Map();

    Array.from($sourceWrapper.get(0)?.attributes || []).forEach((attribute) => {
        sourceAttributes.set(attribute.name, attribute.value);
    });

    Array.from($targetWrapper.get(0)?.attributes || []).forEach((attribute) => {
        if (!sourceAttributes.has(attribute.name)) {
            $targetWrapper.removeAttr(attribute.name);
        }
    });

    sourceAttributes.forEach((value, name) => {
        $targetWrapper.attr(name, value);
    });

    $targetWrapper.html($sourceWrapper.html());

    return $targetWrapper;
}

function placeRoleplayHeaderWrapper($header, $wrapper) {
    if (!$header?.length || !$wrapper?.length) {
        return $wrapper;
    }

    const $users = $header.children('.g-chatform-header-users').first();
    const $back = $header.children('.icon-arrow').first();
    const $credits = $header.children('.growtype-chat-credits').first();
    const $videocall = $header.children('.g-chat-btn-videocall').first();
    const $moreOptions = $header.children('.g-chat-btn-more-options').first();
    const $level = $header.children('.g-chat-level').first();

    if ($users.length) {
        $wrapper.insertAfter($users);
    } else if ($back.length) {
        $wrapper.insertAfter($back);
    } else if ($credits.length) {
        $wrapper.insertBefore($credits);
    } else if ($videocall.length) {
        $wrapper.insertBefore($videocall);
    } else if ($moreOptions.length) {
        $wrapper.insertBefore($moreOptions);
    } else if ($level.length) {
        $wrapper.insertAfter($level);
    } else {
        $header.append($wrapper);
    }

    return $wrapper;
}

export function chatSettings() {
    hydrateChatAjaxConfigFromDom();
    window.updateChatBackground = updateChatBackground;
    window.growtypeChatBackgroundState = {
        getBaseBackgroundState,
        getBaseBackgroundValue,
        restoreBaseBackgroundState,
        rememberCurrentBackgroundState,
    };
    // Initialize voice preview state
    window.growtypeChatSettingsPreviewVoice = null;

    // Initialize Chosen plugin for select elements
    if ($.fn.chosen) {
        $('.chat-settings').find('select').chosen({
            disable_search_threshold: 30,
            width: '100%'
        });
    }
    bindLazyRoleplaySearch();

    // Filter voice dropdown to show only voices from the currently active provider
    // Must run AFTER Chosen is initialized so chosen:updated takes effect
    filterSettingsVoicesByProvider($('select[name="tts_model_used"]'));

    // Store current roleplay key
    activeRoleplayKey = isRoleplayActive()
        ? $(`select[name="${ROLEPLAY_SCENARIO_KEY}"]`).val()
        : 'none';
    const $backgroundSelect = $('select[name="chat_background_value"]');
    baseBackgroundState = readBaseBackgroundState();

    if (!baseBackgroundState) {
        rememberCurrentBackgroundState({ allowDuringRoleplay: false });
    }

    if (!baseBackgroundState && isRoleplayActive()) {
        seedBaseBackgroundStateFromCharacterImage();
    }

    // Sync the "featured_image" option data if a roleplay is active on init
    syncFeaturedImageOption();

    // Apply roleplay's custom background on load if one is already selected
    const $roleplaySelect = $(`select[name="${ROLEPLAY_SCENARIO_KEY}"]`);
    const activeRoleplayOption = $roleplaySelect.find('option:selected');
    const activeRoleplayVal = $roleplaySelect.val();
    let customRoleplayBgApplied = false;

    if (isRoleplayActive() && activeRoleplayVal && activeRoleplayVal !== 'none') {
        const roleplayBgValue = activeRoleplayOption.attr('data-chat-background-value');
        if (roleplayBgValue) {
            const isCustomBgUrl = roleplayBgValue.startsWith('http') || roleplayBgValue.startsWith('/');
            const isFeaturedSelected = $backgroundSelect.val() === 'featured_image';

            if (isCustomBgUrl && isFeaturedSelected) {
                updateBackgroundRowUI(roleplayBgValue);
                updateChatBackground(roleplayBgValue);
                customRoleplayBgApplied = true;
            } else if (!isCustomBgUrl) {
                const targetBgValue = roleplayBgValue;
                if ($backgroundSelect.val() !== targetBgValue) {
                    $backgroundSelect.val(targetBgValue);
                    if ($.fn.chosen) $backgroundSelect.trigger('chosen:updated');
                }
            }
        }
    }

    // Render the initial background choice (skip if a custom roleplay URL was already applied)
    if (!customRoleplayBgApplied) {
        const selectedBackgroundOption = $backgroundSelect.find('option:selected');
        const initialPreviewImage = selectedBackgroundOption.attr('data-preview-image');
        if (initialPreviewImage) {
            updateBackgroundRowUI(initialPreviewImage);
            updateChatBackground(initialPreviewImage);
        }
    }

    // Initialize personality slider values
    initPersonalitySliders();

    // Bind event handlers (using .off().on() to prevent duplicates)
    const modalNavigationOptions = {
        syncRoleplaySettingsTabVisibility,
        syncRoleplaySettingsToggleState,
        stopVoicePreview,
    };
    bindVoicePreviewHandlers();
    bindVoiceSelectPreviewHandlers();
    bindRoleplayPreviewModal();
    bindSelectChangeHandlers();
    bindFormSubmitHandler();
    bindSettingsModalNavigation(modalNavigationOptions);
    bindSliderInputHandler();
    bindMemoriesManager();
    bindBackupsManager();
    initRoleplayDetailsCollapseActions();
    openSettingsModalFromHash(modalNavigationOptions);

    // Listen for roleplay updates from quiz responses
    document.removeEventListener('growtypeChatMessageResponseEvent', handleMessageResponse);
    document.addEventListener('growtypeChatMessageResponseEvent', handleMessageResponse);
}

function bindLazyRoleplaySearch() {
    const $select = $(`select[name="${ROLEPLAY_SCENARIO_KEY}"][data-roleplay-lazy-search="true"]`).first();
    if (!$select.length || !$select.attr('data-roleplay-search-url')) {
        return;
    }

    if ($.fn.chosen && $select.data('chosen')) {
        $select.chosen('destroy').chosen({
            disable_search_threshold: 0,
            width: '100%',
        });
    }

    let request = null;
    let timer = null;
    const $container = $select.next('.chosen-container');
    const $search = $container.find('.chosen-search input');

    $search.off('input.roleplayLazySearch').on('input.roleplayLazySearch', function () {
        const query = String($(this).val() || '').trim();
        clearTimeout(timer);
        if (query.length < 2) {
            return;
        }

        timer = setTimeout(() => {
            request?.abort();
            request = $.ajax({
                url: $select.attr('data-roleplay-search-url'),
                type: 'POST',
                dataType: 'json',
                data: {
                    action: $select.attr('data-roleplay-search-action'),
                    nonce: $select.attr('data-roleplay-search-nonce'),
                    search: query,
                    limit: 25,
                },
            }).done((response) => {
                const items = Array.isArray(response?.data?.items) ? response.data.items : [];
                items.forEach((item) => {
                    const key = String(item?.key || '').trim();
                    const optionExists = $select.find('option').filter(function () {
                        return String($(this).val()) === key;
                    }).length > 0;
                    if (!key || optionExists) {
                        return;
                    }
                    $('<option>')
                        .val(key)
                        .text(item.title || key)
                        .attr({
                            'data-title': item.title || key,
                            'data-description': item.description || '',
                            'data-preview-image': item.image || '',
                            'data-main-image': item.mainImage || item.image || '',
                            'data-chat-background-value': item.backgroundValue || '',
                            'data-caption': item.caption || '',
                            'data-profile-url': item.profileUrl || '',
                            'data-adjust-url': item.adjustUrl || '',
                            'data-characters': item.characters || '[]',
                            'data-locked': item.locked ? '1' : '0',
                        })
                        .appendTo($select);
                });
                $select.trigger('chosen:updated');
                const $nextSearch = $select.next('.chosen-container').find('.chosen-search input');
                $nextSearch.val(query).trigger('keyup');
            });
        }, 200);
    });
}

function syncRoleplaySettingsTabVisibility(isRoleplayEnabled = isRoleplayActive()) {
    const $modal = $('#growtypeChatSettingsModal');
    const $tabItem = $modal.find('[data-roleplay-tab-item="true"]');

    if (!$tabItem.length) {
        return;
    }

    $tabItem.removeClass('d-none');
}

function getRoleplaySettingsSelect($modal = $('#growtypeChatSettingsModal')) {
    return $modal.find(`select[name="${ROLEPLAY_SCENARIO_KEY}"]`).first();
}

function getRoleplaySettingsToggle($modal = $('#growtypeChatSettingsModal')) {
    return $modal.find('.roleplay-toggle-row [data-roleplay-settings-toggle="true"]').first();
}

function getRoleplayChatForm() {
    return jQuery('.g-chatform').first();
}

function getRoleplayRequestState() {
    return (getRoleplayChatForm().attr('data-roleplay-request-state') || 'idle').trim() || 'idle';
}

function syncRoleplaySettingsStateBeforeSubmit($form) {
    const $roleplaySelect = getRoleplaySettingsSelect($form);

    if (!$roleplaySelect.length) {
        return '';
    }

    const $toggle = getRoleplaySettingsToggle($form);
    const isToggleActive = $toggle.length
        ? $toggle.hasClass('is-active')
        || $toggle.attr('data-roleplay-active') === '1'
        || $toggle.find('input[type="checkbox"]').first().is(':checked')
        : false;

    const currentValue = ($roleplaySelect.val() || '').trim();
    const fallbackValue = getLastEnabledRoleplayKey($roleplaySelect) || getFirstAvailableRoleplayKey($roleplaySelect) || 'none';
    const submission = resolveRoleplaySettingsSubmission({
        selectedKey: currentValue,
        fallbackKey: fallbackValue,
        isToggleActive,
    });
    const nextValue = submission.selectedRoleplayKey;

    if (nextValue !== currentValue) {
        $roleplaySelect.val(nextValue);
        if ($.fn.chosen) {
            $roleplaySelect.trigger('chosen:updated');
        }
    }

    if (nextValue !== 'none') {
        setLastEnabledRoleplayKey($roleplaySelect, nextValue);
    }

    return submission.activeRoleplayKey;
}

function getLastEnabledRoleplayKey($select) {
    return ($select.attr('data-roleplay-last-enabled-key') || '').trim();
}

function setLastEnabledRoleplayKey($select, value) {
    const normalizedValue = (value || '').trim();

    if (!normalizedValue || normalizedValue === 'none') {
        return;
    }

    $select.attr('data-roleplay-last-enabled-key', normalizedValue);
}

function getFirstAvailableRoleplayKey($select) {
    if (!$select?.length) {
        return '';
    }

    return $select.find('option').filter(function () {
        const optionValue = (jQuery(this).attr('value') || '').trim();
        return optionValue !== '' && optionValue !== 'none';
    }).first().val() || '';
}

export function syncRoleplaySettingsToggleState(isRoleplayEnabled = isRoleplayActive(), options = {}) {
    const $modal = $('#growtypeChatSettingsModal');
    const $toggle = getRoleplaySettingsToggle($modal);
    const $roleplaySelect = getRoleplaySettingsSelect($modal);
    const requestState = (options.requestState || getRoleplayRequestState()).trim() || 'idle';
    const isLoading = requestState !== 'idle';

    if (!$toggle.length) {
        return;
    }

    const selectedRoleplayKey = ($roleplaySelect.val() || '').trim();
    const active = typeof isRoleplayEnabled === 'boolean'
        ? isRoleplayEnabled
        : (selectedRoleplayKey !== '' && selectedRoleplayKey !== 'none');
    const label = active ? 'Turn roleplay off' : 'Turn roleplay on';
    const fallbackRoleplayKey = getLastEnabledRoleplayKey($roleplaySelect) || getFirstAvailableRoleplayKey($roleplaySelect);
    const roleplayKey = (options.roleplayKey || (active ? selectedRoleplayKey : fallbackRoleplayKey) || fallbackRoleplayKey || '').trim();

    $toggle
        .attr('data-roleplay-active', active ? '1' : '0')
        .attr('data-roleplay-key', roleplayKey || fallbackRoleplayKey)
        .attr('aria-pressed', active ? 'true' : 'false')
        .attr('aria-label', label)
        .attr('title', label)
        .attr('data-roleplay-request-state', requestState)
        .attr('aria-busy', isLoading ? 'true' : 'false')
        .toggleClass('is-loading', isLoading)
        .toggleClass('is-active', active);

    $toggle.find('input[type="checkbox"]').first()
        .prop('checked', active)
        .attr('checked', active ? 'checked' : null);

    if (isLoading) {
        $toggle.attr('aria-disabled', 'true');
    } else {
        $toggle.removeAttr('aria-disabled');
    }
}

// ============================================
// Select Change Handlers
// ============================================

function bindSelectChangeHandlers() {
    $(document)
        .off('change.chatSettingsSelect', '.chat-settings-single select')
        .on('change.chatSettingsSelect', '.chat-settings-single select', function () {
            const $this = $(this);
            const value = $this.val();
            const $settingsSingle = $this.closest('.chat-settings-single');
            const type = $settingsSingle.attr('data-type');
            const selectedOption = $this.find('option:selected');
            const isLocked = selectedOption.attr('data-locked') === '1' || selectedOption.attr('data-locked') === 'true';

            // Toggle locked class for validation on submit
            $settingsSingle.toggleClass('is-locked-selected', isLocked && !$('body').hasClass('active-subscription'));

            if (type === 'roleplay') {
                handleRoleplayChange($this, value, selectedOption, isLocked);
            } else if (type === 'background') {
                handleBackgroundChange(selectedOption);
            }
        });
}

function getBaseBackgroundStorageKey() {
    const path = window.location?.pathname || 'chat';
    return `${BASE_BACKGROUND_STORAGE_PREFIX}${path}`;
}

function normalizeBackgroundState(state) {
    if (!state || typeof state !== 'object') {
        return null;
    }

    const value = (state.value || '').toString().trim();

    if (value === '') {
        return null;
    }

    return {
        value,
        previewImage: (state.previewImage || '').toString().trim(),
        mainImage: (state.mainImage || '').toString().trim(),
        bgImage: (state.bgImage || '').toString().trim(),
        label: (state.label || '').toString().trim(),
        caption: (state.caption || '').toString().trim(),
    };
}

function readBaseBackgroundState() {
    const storageKey = getBaseBackgroundStorageKey();

    try {
        const rawValue = window.sessionStorage?.getItem(storageKey);
        if (!rawValue) {
            return null;
        }

        return normalizeBackgroundState(JSON.parse(rawValue));
    } catch (error) {
        console.warn('[chat-background] Could not read base background state', error);
        return null;
    }
}

function writeBaseBackgroundState(state) {
    const normalizedState = normalizeBackgroundState(state);
    const storageKey = getBaseBackgroundStorageKey();
    baseBackgroundState = normalizedState;

    try {
        if (normalizedState) {
            window.sessionStorage?.setItem(storageKey, JSON.stringify(normalizedState));
        } else {
            window.sessionStorage?.removeItem(storageKey);
        }
    } catch (error) {
        console.warn('[chat-background] Could not persist base background state', error);
    }

    return baseBackgroundState;
}

function buildBackgroundStateFromOption(selectedOption) {
    const $option = jQuery(selectedOption).first();

    if ($option.length === 0) {
        return null;
    }

    return normalizeBackgroundState({
        value: $option.val() || '',
        previewImage: $option.attr('data-preview-image') || '',
        mainImage: $option.attr('data-main-image') || '',
        bgImage: $option.attr('data-bg-image') || '',
        label: $option.attr('data-label') || $option.text() || '',
        caption: $option.attr('data-caption') || '',
    });
}

function getDefaultBaseBackgroundState() {
    const $backgroundSelect = jQuery('select[name="chat_background_value"]').first();

    if ($backgroundSelect.length === 0) {
        return null;
    }

    const stateFromSelect = normalizeBackgroundState({
        value: $backgroundSelect.attr('data-default-background-value') || '',
        previewImage: $backgroundSelect.attr('data-default-background-preview-image') || '',
        mainImage: $backgroundSelect.attr('data-default-background-main-image') || '',
        bgImage: $backgroundSelect.attr('data-default-background-bg-image') || '',
        label: $backgroundSelect.attr('data-default-background-label') || '',
        caption: $backgroundSelect.attr('data-default-background-caption') || '',
    });

    if (stateFromSelect) {
        return stateFromSelect;
    }

    return buildBackgroundStateFromOption(
        $backgroundSelect.find('option[value="' + ($backgroundSelect.attr('data-default-background-value') || '') + '"]').first()
    );
}

function isRoleplayActive() {
    const $chatForm = getRoleplayChatForm();
    if ($chatForm.length) {
        return $chatForm.attr('data-roleplay') === '1';
    }

    const roleplayValue = $(`select[name="${ROLEPLAY_SCENARIO_KEY}"]`).first().val();
    return !!roleplayValue && roleplayValue !== 'none';
}

function rememberCurrentBackgroundState({ selectedOption = null, allowDuringRoleplay = false } = {}) {
    if (!allowDuringRoleplay && isRoleplayActive()) {
        return getBaseBackgroundState();
    }

    const $backgroundSelect = jQuery('select[name="chat_background_value"]').first();
    const state = buildBackgroundStateFromOption(
        selectedOption || $backgroundSelect.find('option:selected').first()
    );

    if (!state) {
        const defaultState = getDefaultBaseBackgroundState();
        return defaultState ? writeBaseBackgroundState(defaultState) : getBaseBackgroundState();
    }

    if (state.value === 'featured_image') {
        const defaultState = getDefaultBaseBackgroundState();
        return defaultState ? writeBaseBackgroundState(defaultState) : getBaseBackgroundState();
    }

    return writeBaseBackgroundState(state);
}

function seedBaseBackgroundStateFromCharacterImage() {
    const $backgroundSelect = jQuery('select[name="chat_background_value"]').first();
    const $selectedOption = $backgroundSelect.find('option:selected').first();

    if (!$selectedOption.length) {
        const defaultState = getDefaultBaseBackgroundState();
        return defaultState ? writeBaseBackgroundState(defaultState) : null;
    }

    const fallbackState = buildBackgroundStateFromOption($selectedOption);

    if (!fallbackState) {
        const defaultState = getDefaultBaseBackgroundState();
        return defaultState ? writeBaseBackgroundState(defaultState) : null;
    }

    if (fallbackState.value === 'featured_image') {
        const defaultState = getDefaultBaseBackgroundState();
        return defaultState ? writeBaseBackgroundState(defaultState) : fallbackState;
    }

    return writeBaseBackgroundState(fallbackState);
}

function syncBackgroundOptionFromState(state) {
    const normalizedState = normalizeBackgroundState(state);

    if (!normalizedState) {
        return;
    }

    const $option = jQuery(`select[name="chat_background_value"] option[value="${normalizedState.value}"]`).first();

    if ($option.length === 0) {
        return;
    }

    const attrs = {
        'data-preview-image': normalizedState.previewImage,
        'data-main-image': normalizedState.mainImage,
        'data-bg-image': normalizedState.bgImage,
        'data-label': normalizedState.label,
        'data-caption': normalizedState.caption,
    };

    Object.entries(attrs).forEach(([attrName, attrValue]) => {
        if (attrValue) {
            $option.attr(attrName, attrValue);
        } else {
            $option.removeAttr(attrName);
        }
    });
}

function getBaseBackgroundState() {
    if (!baseBackgroundState) {
        baseBackgroundState = readBaseBackgroundState();
    }

    return baseBackgroundState || getDefaultBaseBackgroundState();
}

function getBaseBackgroundValue() {
    return getBaseBackgroundState()?.value || '';
}

function restoreBaseBackgroundState({ triggerChange = true, forceApply = false } = {}) {
    const state = getBaseBackgroundState();
    const $backgroundSelect = jQuery('select[name="chat_background_value"]').first();

    if (!state || $backgroundSelect.length === 0) {
        return '';
    }

    syncBackgroundOptionFromState(state);

    if ($backgroundSelect.val() !== state.value) {
        $backgroundSelect.val(state.value);
        if (jQuery.fn.chosen) {
            $backgroundSelect.trigger('chosen:updated');
        }

        if (triggerChange) {
            $backgroundSelect.trigger('change');
        }
    } else if (forceApply) {
        handleBackgroundChange($backgroundSelect.find('option:selected').first());
    }

    return state.value;
}

function handleRoleplayChange($select, value, selectedOption, isLocked) {
    const isNone = value === 'none';
    const characterImage = $select.attr('data-character-image');

    // Get images and caption for the selected option
    const previewImage = isNone ? '' : selectedOption.attr('data-preview-image');
    const mainImage = isNone ? '' : selectedOption.attr('data-main-image');
    const bgPreviewImage = isNone ? characterImage : selectedOption.attr('data-preview-image');
    const bgMainImage = isNone ? characterImage : selectedOption.attr('data-main-image');
    const caption = selectedOption.attr('data-caption') || '';

    if (!isNone) {
        setLastEnabledRoleplayKey($select, value);
    }

    // Update roleplay row UI
    if (isNone) {
        const $roleplayRow = $select.closest('.chat-settings-single');
        $roleplayRow.addClass('preview-is-hidden').removeClass('has-info');
        $roleplayRow.find('.preview-bg').attr('style', '');
        $roleplayRow.find('[data-roleplay-preview-trigger]').attr('data-roleplay-preview-image', '');
    } else {
        const $roleplayRow = $select.closest('.chat-settings-single');
        $roleplayRow.removeClass('preview-is-hidden').addClass('has-info');
        updateRoleplayRowUI(previewImage, caption, ROLEPLAY_SCENARIO_KEY);
    }

    if (!auto_enable_roleplay_on_scenario_change) {
        return;
    }

    const $backgroundSingle = $('.chat-settings-single[data-type="background"]');
    const $backgroundSelect = $backgroundSingle.find('select[name="chat_background_value"]');

    const chatForm = jQuery('.g-chatform');
    chatForm.attr('data-roleplay', isNone ? '0' : '1');
    if (isNone) {
        chatForm.removeClass('has-compact-chat-content');
    }
    syncRoleplaySettingsTabVisibility(!isNone);
    syncRoleplaySettingsToggleState(!isNone);

    $backgroundSingle.show();

    // Update 'featured_image' option in background select
    const $featuredOption = $backgroundSelect.find('option[value="featured_image"]');
    if ($featuredOption.length) {
        const roleplayBgValue = selectedOption.attr('data-chat-background-value');
        const isCustomBgUrl = roleplayBgValue && (roleplayBgValue.startsWith('http') || roleplayBgValue.startsWith('/'));

        const bgImage = isNone ? characterImage : (isCustomBgUrl ? roleplayBgValue : bgPreviewImage);

        $featuredOption.attr('data-preview-image', bgImage);
        $featuredOption.attr('data-main-image', bgImage);
        $featuredOption.attr('data-bg-image', bgImage);
        $featuredOption.attr('data-label', selectedOption.attr('data-label') || selectedOption.text());
        $featuredOption.attr('data-caption', '');
        $featuredOption.attr('data-locked', isLocked ? '1' : '');

        if (isCustomBgUrl && !isNone) {
            if ($backgroundSelect.val() === 'featured_image') {
                updateBackgroundRowUI(roleplayBgValue);
                updateChatBackground(roleplayBgValue);
            }
        } else {
            const targetBgValue = (roleplayBgValue && !isCustomBgUrl) ? roleplayBgValue : 'featured_image';
            if ($backgroundSelect.val() !== targetBgValue) {
                $backgroundSelect.val(targetBgValue).trigger('change');
                if ($.fn.chosen) $backgroundSelect.trigger('chosen:updated');
            } else {
                handleBackgroundChange($backgroundSelect.find('option:selected'));
            }
        }
    }
}

function handleBackgroundChange(selectedOption) {
    const previewImage = selectedOption.attr('data-preview-image');
    const caption = selectedOption.attr('data-caption') || '';
    updateBackgroundRowUI(previewImage, caption);
    updateChatBackground(previewImage);

    if (!isRoleplayActive()) {
        rememberCurrentBackgroundState({ selectedOption });
    }
}

// ============================================
// Sync Featured Image Option
// ============================================

function syncFeaturedImageOption() {
    if (!isRoleplayActive()) {
        return;
    }

    const activeRoleplay = $(`select[name="${ROLEPLAY_SCENARIO_KEY}"]`).val();
    const $backgroundSelect = $('select[name="chat_background_value"]');

    if (activeRoleplay && activeRoleplay !== 'none') {
        const selectedRoleplayOption = $(`select[name="${ROLEPLAY_SCENARIO_KEY}"] option:selected`);
        const roleplayBgValue = selectedRoleplayOption.attr('data-chat-background-value') || '';
        const isCustomBgUrl = roleplayBgValue.startsWith('http') || roleplayBgValue.startsWith('/');

        // Prefer the resolved chat background image; fall back to preview image
        const bgImage = isCustomBgUrl
            ? roleplayBgValue
            : (selectedRoleplayOption.attr('data-main-image') || selectedRoleplayOption.attr('data-preview-image'));
        const previewImage = isCustomBgUrl ? roleplayBgValue : selectedRoleplayOption.attr('data-preview-image');

        if (bgImage) {
            const $featuredOption = $backgroundSelect.find('option[value="featured_image"]');
            if ($featuredOption.length) {
                $featuredOption.attr('data-preview-image', previewImage);
                $featuredOption.attr('data-main-image', bgImage);
                $featuredOption.attr('data-bg-image', bgImage);
                $featuredOption.attr('data-label', selectedRoleplayOption.attr('data-label') || selectedRoleplayOption.text());
                $featuredOption.attr('data-caption', '');
            }
        }
    }
}

// ============================================
// Form Submit Handler
// ============================================

function bindFormSubmitHandler() {
    $('.chat-settings').off('submit').on('submit', function (event) {
        event.preventDefault();
        const settingsForm = $(this);

        if (settingsForm.data('is-submitting')) return;

        const token = $('.g-chatform').attr('data-session-token');
        const keepModalOpen = settingsForm.data('keep-modal-open') === true;
        settingsForm.data('is-submitting', true).addClass('is-loading').find('button[type="submit"]').addClass('is-loading');

        const normalizedRoleplayValue = syncRoleplaySettingsStateBeforeSubmit(settingsForm);
        let settings = {};
        let isLocked = false;

        settingsForm.find('input,textarea,select').each(function () {
            const $element = $(this);
            if ($element.closest('.chat-settings-single').hasClass('is-locked-selected')) {
                isLocked = true;
            }
            if ($element.attr('name')) {
                settings[$element.attr('name')] = $element.attr('type') === 'checkbox' ? $element.is(':checked') : $element.val();
            }
        });

        const selectedRoleplayKey = (getRoleplaySettingsSelect(settingsForm).val() || '').trim();
        settings[ROLEPLAY_SCENARIO_KEY] = normalizedRoleplayValue || 'none';
        if (selectedRoleplayKey && selectedRoleplayKey !== 'none') {
            settings[LAST_EMBEDDED_ROLEPLAY_SCENARIO_KEY] = selectedRoleplayKey;
        }

        if (isLocked) {
            if (confirm("This option requires a premium plan. View available plans?")) {
                window.location.href = '/plans/';
            }

            settingsForm.removeClass('is-loading').find('button[type="submit"]').removeClass('is-loading');
            settingsForm.data('is-submitting', false);
            return;
        }

        // Capture roleplay value at submit time for accurate change detection
        const submittedRoleplayKey = settings[ROLEPLAY_SCENARIO_KEY];

        // Clear URL params but preserve the active roleplay scenario
        if (window.location.search) {
            const urlParams = new URLSearchParams(window.location.search);
            const roleplayScenario = urlParams.get(ROLEPLAY_SCENARIO_KEY);
            const newSearch = roleplayScenario ? `?${ROLEPLAY_SCENARIO_KEY}=` + encodeURIComponent(roleplayScenario) : '';
            window.history.replaceState({}, document.title, window.location.origin + window.location.pathname + newSearch);
        }

        $.ajax({
            url: growtype_chat_ajax.url,
            type: "post",
            data: {
                action: 'growtype_chat_update_settings',
                session: { token, settings },
                nonce: growtype_chat_ajax.nonce
            },
            success: function (response) {
                const roleplayScenario = response?.data?.[ROLEPLAY_SCENARIO_KEY] || '';
                const roleplayDetailsHtml = response?.data?.roleplay_details_html || '';
                const triggerRoleplayIntro = response?.data?.trigger_roleplay_intro || false;
                const roleplayScenarioKey = response?.data?.roleplay_scenario_key || '';
                const roleplayIsActive = response?.data?.roleplay_active;
                const refreshChatContent = Boolean(response?.data?.refresh_chat_content);

                updateRoleplayHeaderUI(roleplayScenario, roleplayDetailsHtml, roleplayIsActive);

                if (roleplayScenario?.preview_image) {
                    updateRoleplayRowUI(roleplayScenario.preview_image, roleplayScenario.caption || '', ROLEPLAY_SCENARIO_KEY);
                }

                settingsForm.data('is-submitting', false).removeClass('is-loading').find('button[type="submit"]').removeClass('is-loading');

                if (response?.success) {
                    if (!keepModalOpen) {
                        $('#growtypeChatSettingsModal').modal('hide');
                    }

                    // Keep the page in place and let the chat refresh happen through events.
                    const roleplayRefresh = resolveRoleplaySettingsRefresh({
                        submittedKey: submittedRoleplayKey,
                        activeKey: activeRoleplayKey,
                        responseKey: roleplayScenarioKey,
                        refreshChatContent,
                    });

                    if (roleplayRefresh.shouldDispatch) {
                        document.dispatchEvent(new CustomEvent('growtypeChatRoleplayStateChanged', {
                            detail: {
                                roleplayKey: roleplayRefresh.roleplayKey,
                                refreshChatContent: true,
                                source: 'chat-settings-submit'
                            }
                        }));
                    }

                    activeRoleplayKey = roleplayScenarioKey || submittedRoleplayKey;
                } else if ($.fn.chosen) {
                    $('.chat-settings').find('select').trigger('chosen:updated');
                }

                settingsForm.removeData('keep-modal-open');
                evaluateChatHeight();
            },
            error: function (response) {
                settingsForm.data('is-submitting', false).removeClass('is-loading').find('button[type="submit"]').removeClass('is-loading');
                if (!keepModalOpen) {
                    $('#growtypeChatSettingsModal').modal('hide');
                }
                settingsForm.removeData('keep-modal-open');
                handleAjaxError(response);
            }
        });
    });
}

// ============================================
// Message Response Handler (Quiz updates)
// ============================================

function handleMessageResponse(event) {
    const evaluation = event.detail?.response?.data?.question_evaluation;
    const roleplayKey = evaluation?.roleplay_scenario_key;
    const roleplayScenario = evaluation?.roleplay_scenario;
    const roleplayBackgroundValue = evaluation?.roleplay_background_value || '';
    const updateBackgroundImage = evaluation?.update_background_image;
    const roleplayDetailsHtml = evaluation?.roleplay_details_html;

    if (!roleplayKey) return;

    const roleplaySelect = jQuery(`select[name="${ROLEPLAY_SCENARIO_KEY}"]`);
    if (!roleplaySelect.length) return;

    roleplaySelect.val(roleplayKey).trigger('chosen:updated');

    const selectedOption = roleplaySelect.find('option:selected');
    const previewImage = selectedOption.attr('data-preview-image') || roleplayScenario?.preview_image;
    const caption = selectedOption.attr('data-caption') || roleplayScenario?.caption || '';

    const $featuredOption = jQuery('select[name="chat_background_value"] option[value="featured_image"]');

    if ($featuredOption.length) {
        const img = roleplayScenario?.preview_image || '';
        $featuredOption.attr('data-preview-image', img);
        $featuredOption.attr('data-main-image', img);
        $featuredOption.attr('data-bg-image', img);
    }

    updateRoleplayRowUI(previewImage, caption, ROLEPLAY_SCENARIO_KEY);
    updateRoleplayHeaderUI(roleplayScenario, roleplayDetailsHtml);
    syncRoleplaySettingsTabVisibility(true);
    syncRoleplaySettingsToggleState(true);

    // Update chat background
    const $bgSelect = jQuery('select[name="chat_background_value"]');
    const isFeaturedSelected = $bgSelect.val() === 'featured_image';

    if (set_roleplay_background || isFeaturedSelected || roleplayKey === 'custom') {
        const roleplayBgValue = roleplayBackgroundValue || selectedOption.attr('data-chat-background-value');
        const isCustomBgUrl = roleplayBgValue && (roleplayBgValue.startsWith('http') || roleplayBgValue.startsWith('/'));

        if (isCustomBgUrl) {
            // Raw URL — apply directly only if featured_image is selected or set_roleplay_background is true
            // but if user has explicitly selected something else, we should respect that.
            if (isFeaturedSelected || set_roleplay_background) {
                updateChatBackground(roleplayBgValue);
            }
        } else {
            const targetBgValue = roleplayBgValue ? roleplayBgValue : 'featured_image';

            if ($bgSelect.val() !== targetBgValue) {
                $bgSelect.val(targetBgValue);
                if (jQuery.fn.chosen) $bgSelect.trigger('chosen:updated');
                $bgSelect.trigger('change');
            } else {
                if (targetBgValue !== 'featured_image') {
                    handleBackgroundChange($bgSelect.find('option:selected'));
                } else {
                    const currentImage = roleplayScenario?.main_image || roleplayScenario?.preview_image || selectedOption.attr('data-main-image') || selectedOption.attr('data-preview-image') || '';
                    const currentPreview = roleplayScenario?.preview_image || selectedOption.attr('data-preview-image') || currentImage;

                    if (updateBackgroundImage) {
                        updateBackgroundRowUI(currentPreview);
                        updateChatBackground(currentImage);
                    }
                }
            }
        }
    }
}

// ============================================
// UI Update Functions
// ============================================


function updateBackgroundRowUI(previewImage, caption = '') {
    const $backgroundRow = jQuery('.chat-settings-single[data-type="background"]');
    if (!$backgroundRow.length || !previewImage) return;

    // Use stable parent selector — data-fancybox may be toggled off
    const $previewLink = $backgroundRow.find('.preview-bg').parent('a');
    const $previewBg = $previewLink.find('.preview-bg');

    const cleanValue = previewImage.trim().replace(/;$/, '');
    const isImageUrl = cleanValue.startsWith('http') || cleanValue.startsWith('/');
    const isGradient = cleanValue.includes('gradient(');

    if ($previewLink.length) {
        if (isImageUrl) {
            $previewLink.attr('href', cleanValue);
            $previewLink.attr('data-fancybox', 'background-settings');
            $previewLink.attr('data-width', '400');
        } else {
            $previewLink.removeAttr('href');
            $previewLink.removeAttr('data-fancybox');
            $previewLink.removeAttr('data-width');
        }

        // Always clear caption on background link
        $previewLink.attr('data-caption', '').data('caption', '');

        // Rebind Fancybox if available
        if (typeof Fancybox !== 'undefined') {
            Fancybox.unbind($previewLink[0]);
            if (isImageUrl) Fancybox.bind($previewLink[0]);
        }
    }

    if ($previewBg.length) {
        if (isImageUrl) {
            $previewBg.css({
                'background-image': `url('${cleanValue}')`,
                'background-size': 'cover',
                'background-position': 'center',
                'background-color': ''
            });
        } else if (isGradient) {
            $previewBg.css({
                'background-image': cleanValue,
                'background-size': 'cover',
                'background-position': 'center',
                'background-color': ''
            });
        } else {
            $previewBg.css({
                'background-color': cleanValue === 'none' ? 'transparent' : cleanValue,
                'background-image': '',
                'background-size': '',
                'background-position': ''
            });
        }
    }
}

function resolveUrlRoleplayBackground(backgroundValue = '') {
    const urlRoleplay = new URLSearchParams(window.location.search).get(ROLEPLAY_SCENARIO_KEY);

    if (!urlRoleplay) {
        return backgroundValue;
    }

    const $backgroundSelect = jQuery('select[name="chat_background_value"]').first();
    const selectedValue = $backgroundSelect.val();

    // URL roleplays use the persisted background setting. Do not let a
    // roleplay preview/cover image repaint a selected color or gradient.
    if (!selectedValue || selectedValue === 'featured_image') {
        return backgroundValue;
    }

    const selectedPreview = $backgroundSelect.find('option:selected').attr('data-preview-image');

    return selectedPreview || backgroundValue;
}

function updateChatBackground(backgroundImage, roleplayScenario = {}, force = false) {
    const requestedBackground = backgroundImage || roleplayScenario?.preview_image || '';
    const backgroundValue = resolveUrlRoleplayBackground(requestedBackground);
    const cleanValue = (backgroundValue || '').trim().replace(/;$/, '');

    // Prevent redundant updates, but allow forcing if needed
    if (!force && cleanValue === currentBackgroundValue && cleanValue !== '') return;
    currentBackgroundValue = cleanValue;

    const chatForm = jQuery('.g-chatform');
    if (!chatForm.length) return;

    // Initialize layers if they don't exist
    if (chatForm.find('.g-chatform-bg-layers').length === 0) {
        chatForm.prepend(`
            <div class="g-chatform-bg-layers">
                <div class="bg-layer layer-1"></div>
                <div class="bg-layer layer-2"></div>
            </div>
        `);
    }

    const layersContainer = chatForm.find('.g-chatform-bg-layers');
    const layers = layersContainer.find('.bg-layer');

    // Determine active and next layers
    let activeLayer = layers.filter('.is-active');
    let nextLayer = layers.not('.is-active').first();

    // Fallback if no layer is active yet
    if (activeLayer.length === 0) {
        nextLayer = layers.first();
    }

    // If somehow we have no next layer (e.g. both active), just pick the first one
    if (!nextLayer.length) {
        nextLayer = layers.first();
    }
    if (backgroundValue && backgroundValue !== 'none') {
        const cleanBgLower = cleanValue.toLowerCase();
        const isVideoUrl = cleanBgLower.endsWith('.mp4') || cleanBgLower.endsWith('.webm') || cleanBgLower.includes('.mp4?') || cleanBgLower.includes('.webm?');
        const isImageUrl = /^https?:\/\//.test(cleanValue) || cleanValue.startsWith('/');
        const isGradient = cleanValue.includes('gradient(') || cleanValue.includes('rgb(');

        const isRoleplayActive = chatForm.attr('data-roleplay') === '1';
        const isFeaturedImage = jQuery('select[name="chat_background_value"]').val() === 'featured_image';

        // Toggle class based on whether we actually have a custom roleplay background active or a featured image background
        chatForm.toggleClass('has-compact-chat-content', isFeaturedImage || isImageUrl || isVideoUrl);

        // Toggle class for image (or video) vs colour/gradient backgrounds
        chatForm.toggleClass('has-image-background', isImageUrl || isVideoUrl);

        if (isVideoUrl) {
            nextLayer.css({
                'background-image': 'none',
                'background-color': '#000'
            });

            let videoEl = nextLayer.find('video.chat-bg-video');
            if (videoEl.length === 0) {
                videoEl = jQuery('<video class="chat-bg-video" autoplay loop muted playsinline></video>');
                nextLayer.append(videoEl);
            }

            if (videoEl.attr('src') !== cleanValue) {
                videoEl.attr('src', cleanValue);
                videoEl[0].load();
                videoEl[0].play().catch(e => console.log('Video play error:', e));
            }
        } else {
            nextLayer.find('video.chat-bg-video').remove();

            if (isImageUrl) {
                nextLayer.css({
                    'background-image': `url('${cleanValue}')`,
                    'background-size': 'cover',
                    'background-position': 'center',
                    'background-color': ''
                });
            } else if (isGradient) {
                nextLayer.css({
                    'background-image': cleanValue,
                    'background-color': ''
                });
            } else {
                nextLayer.css({
                    'background-color': cleanValue,
                    'background-image': '',
                    'background-size': '',
                    'background-position': ''
                });
            }
        }

        // Cross-dissolve animation
        nextLayer.addClass('is-active');
        activeLayer.not(nextLayer).removeClass('is-active');
    } else {
        chatForm.removeClass('has-compact-chat-content has-image-background');
        layers.removeClass('is-active').css('background', '');
    }
}

function updateRoleplayHeaderUI(roleplayScenario, roleplayDetailsHtml, roleplayIsActive = null) {
    const roleplayPreviewImage = roleplayScenario?.preview_image || '';
    const isActive = typeof roleplayIsActive === 'boolean'
        ? roleplayIsActive
        : Boolean(roleplayPreviewImage);
    const $chatForm = jQuery('.g-chatform');
    const $header = jQuery('.g-chatform-header');
    const $existingWrapper = getCanonicalRoleplayHeaderWrapper($header);
    const hasRenderableHtml = typeof roleplayDetailsHtml === 'string' && roleplayDetailsHtml.trim().length > 0;

    jQuery('.g-chatform-header-users-single p span').remove();

    if (!roleplayPreviewImage) {
        $chatForm.attr('data-roleplay', '0');
        if ($existingWrapper.length) {
            $existingWrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], .roleplay-details-toggle')
                .attr('data-roleplay-active', '0')
                .attr('aria-pressed', 'false')
                .attr('aria-label', 'Turn roleplay on')
                .attr('title', 'Turn roleplay on')
                .removeClass('is-active');
            $existingWrapper.find('input[type="checkbox"]').prop('checked', false).removeAttr('checked');
        }
        return;
    }

    $chatForm.attr('data-roleplay', isActive ? '1' : '0');

    if (hasRenderableHtml) {
        const $nextWrapper = jQuery(roleplayDetailsHtml);

        if ($existingWrapper.length) {
            syncRoleplayHeaderWrapperInPlace($existingWrapper, $nextWrapper);
            placeRoleplayHeaderWrapper($header, $existingWrapper);
        } else {
            placeRoleplayHeaderWrapper($header, $nextWrapper);
        }
    }

    const $activeWrapper = getCanonicalRoleplayHeaderWrapper($header);
    const $roleplayImage = $activeWrapper.find('.roleplay-image').first();

    if (!isActive) {
        $activeWrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], .roleplay-details-toggle')
            .attr('data-roleplay-active', '0')
            .attr('aria-pressed', 'false')
            .attr('aria-label', 'Turn roleplay on')
            .attr('title', 'Turn roleplay on')
            .removeClass('is-active');
        $activeWrapper.find('input[type="checkbox"]').prop('checked', false).removeAttr('checked');
    }

    if ($roleplayImage.length > 0) {
        const previewCss = `background-image: url('${roleplayPreviewImage}');background-size: cover;background-position: center;`;
        $roleplayImage.attr('style', previewCss);
    }
}
